import { ICompanyGroupOption, ICompanyUserGroup } from '../../app/shared/user/dto/ICompanyUserGroup';
import { useState } from 'react';

export type SelectedOptionsMap = Record<number, ICompanyGroupOption[]>;

export function useGroupOptions() {
    const [selectedOptionsMap, setSelectedOptionsMap] = useState<SelectedOptionsMap>({});
    const [autocompleteKeys, setAutocompleteKeys] = useState<Record<number, number>>({});

    function getAvailableOptions(group: ICompanyUserGroup): ICompanyGroupOption[] {
        const selected = selectedOptionsMap[group.id] ?? [];
        const selectedIds = new Set(selected.map(o => o.id));
        return group.options.filter(o => !selectedIds.has(o.id));
    }

    function handleOptionSelect(group: ICompanyUserGroup, selectedOption: ICompanyGroupOption) {
        setSelectedOptionsMap(prev => {
            const current = prev[group.id] ?? [];
            if (!group.isMultiple) {
                return { ...prev, [group.id]: [selectedOption] };
            }
            const alreadyAdded = current.some(o => o.id === selectedOption.id);
            if (alreadyAdded) return prev;
            return { ...prev, [group.id]: [...current, selectedOption] };
        });
        setAutocompleteKeys(prev => ({ ...prev, [group.id]: (prev[group.id] ?? 0) + 1 }));
    }

    function handleOptionRemove(groupId: number, optionId: number) {
        setSelectedOptionsMap(prev => ({
            ...prev,
            [groupId]: (prev[groupId] ?? []).filter(o => o.id !== optionId),
        }));
        setAutocompleteKeys(prev => ({ ...prev, [groupId]: (prev[groupId] ?? 0) + 1 }));
    }
    function buildInitialMap(groups: ICompanyUserGroup[]): SelectedOptionsMap {
        const initialMap: SelectedOptionsMap = {};
        for (const group of groups) {
            const selected = group.options.filter(o => o.isSelected);
            if (selected.length === 0) continue;
            initialMap[group.id] = group.isMultiple ? selected : [selected[0]];
        }
        return initialMap;
    }

    function initSelectedOptions(groups: ICompanyUserGroup[]) {
        setSelectedOptionsMap(buildInitialMap(groups));
    }

    function resetOptions(initialMap?: SelectedOptionsMap) {
        setSelectedOptionsMap(initialMap ?? {});
        setAutocompleteKeys({});
    }

    const flatOptionIds = Object.values(selectedOptionsMap)
        .flat()
        .map(o => o.id);
    return {
        selectedOptionsMap,
        autocompleteKeys,
        flatOptionIds,
        getAvailableOptions,
        handleOptionSelect,
        handleOptionRemove,
        initSelectedOptions,
        resetOptions,
        buildInitialMap,
    };
}
